import React, { useState, useEffect, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBook, faTimes, faTags, faLock } from "@fortawesome/free-solid-svg-icons";
import service from "../../utils/request";
import message from "../../utils/message";

export default function CheckOutModal({
    isOpen,
    onClose,
    onStateChange,
    item,
    items,
    policyId,
    setFileList,
}) {
    const [assignedTopics, setAssignedTopics] = useState([]);
    const [fileTopicsMap, setFileTopicsMap] = useState({});
    const [fileNamesMap, setFileNamesMap] = useState({});
    const [selectedPolicyName, setSelectedPolicyName] = useState("");
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    // Normalize input item / items to array of non-folder items
    const itemList = useMemo(() => {
        let rawList = [];
        if (items) {
            rawList = Array.isArray(items) ? items : Object.values(items);
        } else if (item) {
            rawList = [item];
        }
        return rawList.filter((i) => i && !i.is_folder);
    }, [items, item]);

    useEffect(() => {
        if (isOpen && itemList.length > 0) {
            fetchData();
        }
    }, [isOpen, itemList.length]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const policiesRes = await service.get("/api/policy/file_policy");

            const targetPolicyId = policyId || itemList[0]?.policy_id;
            const policy = (policiesRes || []).find((p) => String(p.key) === String(targetPolicyId));
            setSelectedPolicyName(policy ? policy.value : "Unknown Policy");

            // Fetch topics and fresh file info for each file
            const fileDetails = await Promise.all(
                itemList.map(async (it) => {
                    try {
                        const [topicsRes, itemInfoRes] = await Promise.allSettled([
                            service.get(`/api/topic/topics_by_file?item_id=${it.item_id}`),
                            service.get(`/filer/item/${it.item_id}`)
                        ]);
                        const topics = topicsRes.status === "fulfilled" ? topicsRes.value || [] : [];
                        const itemInfo = itemInfoRes.status === "fulfilled" ? itemInfoRes.value : null;
                        return {
                            itemId: it.item_id,
                            topics,
                            itemName: itemInfo?.item_name
                        };
                    } catch (e) {
                        return { itemId: it.item_id, topics: [], itemName: it.item_name };
                    }
                })
            );

            const topicsMap = {};
            const namesMap = {};
            fileDetails.forEach((r) => {
                topicsMap[r.itemId] = r.topics;
                namesMap[r.itemId] = r.itemName;
            });
            setFileTopicsMap(topicsMap);
            setFileNamesMap(namesMap);

            if (itemList.length === 1) {
                setAssignedTopics(fileDetails[0]?.topics || []);
            }
        } catch (err) {
            console.error("Error loading checkout details:", err);
            message("error", "Failed to load checkout details");
        } finally {
            setLoading(false);
        }
    };

    const handleCheckOut = async () => {
        setActionLoading(true);
        message("loading", "Checking out from library...");
        try {
            await Promise.all(
                itemList.map((it) => service.post(`/api/library/checkout?item_id=${it.item_id}`))
            );
            message(
                "success",
                itemList.length === 1
                    ? "Removed from Library successfully!"
                    : `Checked out ${itemList.length} files successfully!`
            );

            if (onStateChange) {
                onStateChange(false, null);
            }
            if (setFileList) {
                const itemIds = new Set(itemList.map((i) => i.item_id));
                setFileList((prev) =>
                    prev.map((it) =>
                        itemIds.has(it.item_id)
                            ? { ...it, is_checkin: false, policy_id: null }
                            : it
                    )
                );
            }
            onClose();
        } catch (err) {
            console.error("Check-out failed:", err);
            message("error", err?.message || "Failed to check out file");
        } finally {
            setActionLoading(false);
        }
    };

    if (!isOpen || itemList.length === 0) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm transition-all duration-300"
            onClick={onClose}
            onDoubleClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            onKeyUp={(e) => e.stopPropagation()}
        >
            <div
                className="relative w-full max-w-md bg-white rounded-xl shadow-2xl border border-zinc-200 overflow-hidden transform transition-all duration-300 flex flex-col max-h-[90vh]"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                onKeyUp={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100 bg-zinc-50/50">
                    <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faBook} className="text-[#FF8C39] w-4 h-4" />
                        <h3 className="text-lg font-semibold text-zinc-950">Check out</h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 transition-colors"
                    >
                        <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-5 overflow-y-auto flex-1">
                    {itemList.length === 1 ? (
                        <>
                            {/* Single File Name */}
                            <div className="text-start">
                                <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider block mb-1">
                                    File
                                </span>
                                <div
                                    className="text-sm font-medium text-zinc-800 bg-zinc-50 px-3 py-2 rounded-lg border border-zinc-100 truncate"
                                    title={fileNamesMap[itemList[0]?.item_id]}
                                >
                                    {fileNamesMap[itemList[0]?.item_id]}
                                </div>
                            </div>

                            {loading ? (
                                <div className="py-20 flex flex-col items-center justify-center gap-3">
                                    <div className="w-8 h-8 border-4 border-[#FF8C39] border-t-transparent rounded-full animate-spin"></div>
                                    <span className="text-xs text-zinc-500 font-medium">Loading details...</span>
                                </div>
                            ) : (
                                <>
                                    {/* Policy */}
                                    <div className="text-start">
                                        <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider block mb-1">
                                            Policy
                                        </span>
                                        <div className="flex items-center justify-between text-sm font-semibold text-zinc-700 bg-amber-50/40 px-3 py-2.5 rounded-lg border border-amber-200/50">
                                            <span>{selectedPolicyName}</span>
                                            <FontAwesomeIcon icon={faLock} className="text-[#FF8C39]/80 w-3.5 h-3.5" />
                                        </div>
                                    </div>

                                    {/* Topics */}
                                    <div className="text-start">
                                        <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider block mb-1.5">
                                            Topics
                                        </span>
                                        {assignedTopics.length > 0 ? (
                                            <div className="flex flex-wrap gap-1.5 p-3 rounded-lg border border-zinc-200 bg-zinc-50/50">
                                                {assignedTopics.map((t) => (
                                                    <span
                                                        key={t.topic_id}
                                                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-[#FF8C39]/10 text-[#FF8C39] border border-[#FF8C39]/20"
                                                    >
                                                        <FontAwesomeIcon icon={faTags} className="w-2.5 h-2.5" />
                                                        {t.topic_name}
                                                    </span>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="flex flex-col items-center justify-center py-6 px-4 rounded-lg border border-dashed border-zinc-200 text-zinc-400 bg-zinc-50/50">
                                                <FontAwesomeIcon icon={faTags} className="w-5 h-5 mb-1.5 text-zinc-300" />
                                                <span className="text-xs font-medium">No topics assigned</span>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            {loading ? (
                                <div className="py-20 flex flex-col items-center justify-center gap-3">
                                    <div className="w-8 h-8 border-4 border-[#FF8C39] border-t-transparent rounded-full animate-spin"></div>
                                    <span className="text-xs text-zinc-500 font-medium">Loading details...</span>
                                </div>
                            ) : (
                                <>
                                    {/* Policy */}
                                    <div className="text-start">
                                        <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider block mb-1">
                                            Policy
                                        </span>
                                        <div className="flex items-center justify-between text-sm font-semibold text-zinc-700 bg-amber-50/40 px-3 py-2.5 rounded-lg border border-amber-200/50">
                                            <span>{selectedPolicyName}</span>
                                            <FontAwesomeIcon icon={faLock} className="text-[#FF8C39]/80 w-3.5 h-3.5" />
                                        </div>
                                    </div>

                                    {/* Files & Topics List */}
                                    <div className="text-start">
                                        <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider block mb-1.5">
                                            Files & Assigned Topics ({itemList.length})
                                        </span>
                                        <div className="flex flex-col gap-2.5 max-h-60 overflow-y-auto pr-1">
                                            {itemList.map((it) => {
                                                const fileTopics = fileTopicsMap[it.item_id] || [];
                                                return (
                                                    <div
                                                        key={it.item_id}
                                                        className="p-3 bg-zinc-50 rounded-lg border border-zinc-200/80 flex flex-col gap-1.5"
                                                    >
                                                        <div
                                                            className="text-sm font-medium text-zinc-800 truncate"
                                                            title={fileNamesMap[it.item_id]}
                                                        >
                                                            {fileNamesMap[it.item_id]}
                                                        </div>
                                                        {fileTopics.length > 0 ? (
                                                            <div className="flex flex-wrap gap-1.5">
                                                                {fileTopics.map((t) => (
                                                                    <span
                                                                        key={t.topic_id}
                                                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-[#FF8C39]/10 text-[#FF8C39] border border-[#FF8C39]/20"
                                                                    >
                                                                        <FontAwesomeIcon
                                                                            icon={faTags}
                                                                            className="w-2.5 h-2.5"
                                                                        />
                                                                        {t.topic_name}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <span className="text-xs text-zinc-400 italic">
                                                                No topics assigned
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-zinc-100 bg-zinc-50/50">
                    <button
                        onClick={onClose}
                        disabled={actionLoading}
                        className="px-4 py-2 text-sm font-semibold text-zinc-600 hover:text-zinc-800 hover:bg-zinc-100 rounded-lg transition-all"
                    >
                        Close
                    </button>
                    <button
                        onClick={handleCheckOut}
                        disabled={actionLoading || loading}
                        className="px-4 py-2 text-sm font-semibold text-white bg-[#FF8C39] hover:bg-[#e67a2f] disabled:bg-zinc-300 disabled:cursor-not-allowed rounded-lg shadow-sm transition-all flex items-center justify-center"
                    >
                        {actionLoading
                            ? "Checking Out..."
                            : itemList.length > 1
                                ? "Check Out All"
                                : "Check Out"}
                    </button>
                </div>
            </div>
        </div>
    );
}
